import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Col,
  Divider,
  Space,
  Tooltip,
  Row,
  DatePicker,
  Select,
  Button,
  Popover,
} from "antd";
import { RedoOutlined, EnvironmentOutlined } from "@ant-design/icons";
import moment from "moment-timezone";

import useDataTable from "../../../components/common/DataTable";
import {
  fetchReport,
  resetReport,
} from "../../../api/redux/action/ReportActions";
import useNotification from "../../../components/common/ResultNotifier";
import { fetchMobileReaders } from "../../../api/redux/action/MobileReaderActions";
import Header from "../../../components/common/ReportHeader";
import { toTimestamp } from "../../layout/Constants";
import { ElementIDS } from "../../../components/common/ElementsIDs";
import VehicleMovementFirstSeenMap from "./VehicleMovementFirstSeenMap";
import VehicleMovementLastSeenMap from "./VehicleMovementLastSeenMap";
import { exportRequest } from "../../../api/network/axios/exportRequest";

const dateFormat = "DD-MM-YYYY";

const { RangePicker } = DatePicker;

const { Option } = Select;

function VehicleMovement() {
  const today = new Date();
  const todayStr =
    today.getDate() + "-" + (today.getMonth() + 1) + "-" + today.getFullYear();

  const dispatch = useDispatch();

  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [flattenedAllData, setFlattenedAllData] = useState([]);
  const [flattenedData, setFlattenedData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [sortedMobileReaders, setSortedMobileReaders] = useState([]);
  const [dateValue, setDateValue] = useState([]);
  const [vehicleValue, setVehicleValue] = useState();
  const [missingFieldsText, setMissingFieldsText] = useState();

  const [filter, setFilter] = useState({
    fromDate: todayStr,
    toDate: todayStr,
    vehicleRegNos: null,
  });

  const {
    user,
    data,
    allData,
    error,
    requestStatus,
    mobileReaders,
    loading,
    userTimeZone,
    language,
    englishLang,
  } = useSelector((store) => ({
    user: store.authReducer.user,
    data: store.reportReducer.data,
    allData: store.reportReducer.allData,
    error: store.reportReducer.error,
    requestStatus: store.reportReducer.requestStatus,
    mobileReaders: store.mobileReaderReducer.mobileReaders,
    loading: store.reportReducer.loading,
    userTimeZone: store.authReducer.userTimeZone,
    language: store.langReducer.language,
    englishLang: store.langReducer.englishLang,
  }));

  const columns = [
    {
      title: language?.ASSET_GROUP?.value ?? englishLang?.ASSET_GROUP?.value,
      dataIndex: ["asset", "assetGroup", "name"],
    },
    {
      title: language?.ASSET_CODE?.value ?? englishLang?.ASSET_CODE?.value,
      dataIndex: ["asset", "code"],
    },
    {
      title: language?.ASSET?.value ?? englishLang?.ASSET?.value,
      dataIndex: ["asset", "name"],
    },
    {
      title:
        language?.FIRST_DETECTED?.value ?? englishLang?.FIRST_DETECTED?.value,
      dataIndex: "firstSeen",
      render: (firstSeen) => (
        <span>
          {firstSeen
            ? moment.tz(firstSeen, userTimeZone).format("DD-MM-YYYY HH:mm:ss")
            : ""}
        </span>
      ),
    },

    {
      title:
        language?.FIRST_DETECTED_LOCATION?.value ??
        englishLang?.FIRST_DETECTED_LOCATION?.value,
      dataIndex: ["firstSeenLocation"],
      render: (firstSeenLocation, assetId) => (
        <span>
          {firstSeenLocation &&
          firstSeenLocation.latitude &&
          firstSeenLocation.latitude ? (
            <Popover
              trigger="click"
              title={assetId.vehicleRegNo}
              content={
                <VehicleMovementFirstSeenMap
                  lat={firstSeenLocation.latitude}
                  long={firstSeenLocation.longitude}
                />
              }
            >
              <EnvironmentOutlined
                style={{ marginRight: "10px", color: "#1890ff" }}
              />
            </Popover>
          ) : (
            ""
          )}
          {firstSeenLocation && firstSeenLocation.address}
        </span>
      ),
    },
    {
      title:
        language?.LAST_DETECTED?.value ?? englishLang?.LAST_DETECTED?.value,
      dataIndex: "lastSeen",
      render: (lastSeen) => (
        <span>
          {lastSeen
            ? moment.tz(lastSeen, userTimeZone).format("DD-MM-YYYY HH:mm:ss")
            : ""}
        </span>
      ),
    },

    {
      title:
        language?.LAST_DETECTED_LOCATION?.value ??
        englishLang?.LAST_DETECTED_LOCATION?.value,
      dataIndex: ["lastSeenLocation"],
      render: (lastSeenLocation, assetId) => (
        <span>
          {lastSeenLocation &&
          lastSeenLocation.latitude &&
          lastSeenLocation.latitude ? (
            <Popover
              trigger="click"
              title={assetId.vehicleRegNo}
              content={
                <VehicleMovementLastSeenMap
                  lat={lastSeenLocation.latitude}
                  long={lastSeenLocation.longitude}
                />
              }
            >
              <EnvironmentOutlined
                style={{ marginRight: "10px", color: "#1890ff" }}
              />
            </Popover>
          ) : (
            ""
          )}
          {lastSeenLocation && lastSeenLocation.address}
        </span>
      ),
    },
  ];

  useEffect(() => {
    dispatch(resetReport());
    dispatch(fetchMobileReaders());
  }, []);

  useEffect(() => {
    if (mobileReaders && mobileReaders.content) {
      let sorted = mobileReaders.content.sort((a, b) =>
        a.vehicleRegistrationNumber.localeCompare(
          b.vehicleRegistrationNumber,
          undefined,
          { numeric: true, sensitivity: "base" }
        )
      );
      setSortedMobileReaders(sorted);
    }
  }, [mobileReaders]);

  const [ResultNotifier, isLoading] = useNotification(error, requestStatus);

  const {
    DataTable,
    hasSelected,
    currentPage,
    pageSize,
    searchFormHelperHandler,
  } = useDataTable({
    columns,
    dataSource: data,
    updateEntityPath: "",
    filter,
    loading: loading,
    missingFieldsText: missingFieldsText,
  });

  const checkNoMissingParams = () => {
    if (!(filter.fromDate && filter.toDate && filter.vehicleRegNos)) {
      setMissingFieldsText(
        language?.SELECT_DATE_RANGE_VEHICLE?.value ??
          englishLang?.SELECT_DATE_RANGE_VEHICLE?.value
      );
      return false;
    }
    return true;
  };

  useEffect(() => {
    // dispatch(resetReport());
    if (checkNoMissingParams()) {
      setMissingFieldsText();
      dispatch(
        fetchReport({
          page: searchFormHelperHandler(),
          size: pageSize,
          fromDate: toTimestamp(filter.fromDate, "start"),
          toDate: toTimestamp(filter.toDate, "end"),
          vehicleRegNos: filter.vehicleRegNos,
          endpoint: "vehicle-movement",
        })
      );
    }
  }, [currentPage, filter, isLoading, refresh, pageSize]);

  const handleExportAllButtonClick = () => {
    setExportAllLoading(true);
    const exportName = "export-vehicle-movement";
    let options = {
      url: "/api/web/reports/vehicle-movement/export",
      method: "GET",
      responseType: "blob", // important\
      withCredentials: true,
      headers: {
        Accept: "application/vnd.handson.rfid.v5+json",
      },
    };

    options = {
      ...options,
      params: {
        fromDate: toTimestamp(filter.fromDate, "start"),
        toDate: toTimestamp(filter.toDate, "end"),
        vehicleRegNos: filter.vehicleRegNos,
      },
    };
    exportRequest(options, setExportAllLoading, exportName);
  };

  const handleDateRangeChange = (date, dateString) => {
    setDateValue(date);
    setFilter({
      ...filter,
      fromDate: dateString[0],
      toDate: dateString[1],
    });
  };

  const handleVehicleChange = (value) => {
    setVehicleValue(value);
    setFilter({
      ...filter,
      vehicleRegNos: value,
    });
  };

  const handleRefreshClick = () => {
    setRefresh(!refresh);
  };

  useEffect(() => {
    if (data && Object.keys(data).length !== 0) {
      let flatData;
      function flatDataHandler() {
        if (data.content === undefined) {
          return (flatData = Object.values(data));
        } else {
          return (flatData = Object.values(data.content));
        }
      }
      flatDataHandler();

      flatData.map((item) => {
        item.firstSeenFormatted = item.firstSeen
          ? moment
              .tz(item.firstSeen, userTimeZone)
              .format("DD-MM-YYYY HH:mm:ss")
          : "";
        item.lastSeenFormatted = item.lastSeen
          ? moment.tz(item.lastSeen, userTimeZone).format("DD-MM-YYYY HH:mm:ss")
          : "";
      });

      setFlattenedData(Object.values(flatData));
    }
  }, [data]);

  useEffect(() => {
    if (allData && Object.keys(allData).length !== 0) {
      let flatData = Object.values(allData.content);

      flatData.map((item) => {
        item.firstSeenFormatted = item.firstSeen
          ? moment
              .tz(item.firstSeen, userTimeZone)
              .format("DD-MM-YYYY HH:mm:ss")
          : "";
        item.lastSeenFormatted = item.lastSeen
          ? moment.tz(item.lastSeen, userTimeZone).format("DD-MM-YYYY HH:mm:ss")
          : "";
      });

      setFlattenedAllData(Object.values(flatData));
    }
  }, [allData]);

  const headers = [
    {
      label: language?.ASSET_GROUP?.value ?? englishLang?.ASSET_GROUP?.value,
      key: "asset.assetGroup.name",
    },
    {
      label: language?.ASSET_CODE?.value ?? englishLang?.ASSET_CODE?.value,
      key: "asset.code",
    },
    {
      label: language?.ASSET?.value ?? englishLang?.ASSET?.value,
      key: "asset.name",
    },
    {
      label:
        language?.FIRST_DETECTED?.value ?? englishLang?.FIRST_DETECTED?.value,
      key: "firstSeenFormatted",
    },
    {
      label:
        language?.FIRST_DETECTED_ADDRESS?.value ??
        englishLang?.FIRST_DETECTED_ADDRESS?.value,
      key: "firstSeenLocation.address",
    },
    {
      label:
        language?.LAST_DETECTED?.value ?? englishLang?.LAST_DETECTED?.value,
      key: "lastSeenFormatted",
    },
    {
      label:
        language?.LAST_DETECTED_ADDRESS?.value ??
        englishLang?.LAST_DETECTED_ADDRESS?.value,
      key: "lastSeenLocation.address",
    },
  ];

  return (
    <>
      <div style={{ padding: 24, background: "#fff", minHeight: 20 }}>
        <Header
          addNewPath=""
          hasSelected={hasSelected}
          exportTableTitle={
            language?.VEHICLEMOVEMENT?.value ??
            englishLang?.VEHICLEMOVEMENT?.value
          }
          addNewText=""
          title={
            language?.VEHICLE_MOVEMENT?.value ??
            englishLang?.VEHICLE_MOVEMENT?.value
          }
          csvData={flattenedData}
          csvAllData={flattenedAllData}
          csvHeaders={headers}
          exportAllLoading={exportAllLoading}
          loading={loading}
          exportAllButtonClick={handleExportAllButtonClick}
        />
        <Row>
          <Col>
            <Space>
              <Tooltip
                title={
                  language?.SELECT_DATE_RANGE?.value ??
                  englishLang?.SELECT_DATE_RANGE?.value
                }
              >
                <RangePicker
                  id={ElementIDS().calendar}
                  defaultValue={[
                    moment(todayStr, dateFormat),
                    moment(todayStr, dateFormat),
                  ]}
                  format={dateFormat}
                  onChange={handleDateRangeChange}
                />
              </Tooltip>
              <Tooltip
                title={
                  language?.SELECT_A_VEHICLE?.value ??
                  englishLang?.SELECT_A_VEHICLE?.value
                }
              >
                <Select
                  id={ElementIDS().selectVehicle}
                  //   defaultValue="All"
                  // showSearch
                  style={{ width: 200 }}
                  placeholder={
                    language?.SELECT_A_VEHICLE?.value ??
                    englishLang?.SELECT_A_VEHICLE?.value
                  }
                  optionFilterProp="children"
                  onChange={handleVehicleChange}
                  showSearch
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {sortedMobileReaders
                    ? sortedMobileReaders.map((item) => (
                        <Option
                          key={item.externalVehicleReferenceId}
                          value={item.vehicleRegistrationNumber}
                        >
                          {item.vehicleRegistrationNumber}
                        </Option>
                      ))
                    : undefined}
                </Select>
              </Tooltip>
              <Button
                id={ElementIDS().refresh}
                type="primary"
                onClick={() => handleRefreshClick()}
              >
                {" "}
                <RedoOutlined />
                {language?.REFRESH?.value ?? englishLang?.REFRESH?.value}
              </Button>
            </Space>
          </Col>
        </Row>
        <Divider />
        <DataTable />
        <ResultNotifier />
      </div>
    </>
  );
}

export default VehicleMovement;
